import axios, { AxiosResponse } from 'axios';
import {
  LoginRequest,
  LoginResponse,
  LicenseResponse,
  LicenseUpdateRequest,
  NewLicenseRequest,
  NewV2LicenseRequest,
  V2LicenseUpdateRequest,
  GenericResponse,
  QuotaRequest,
  SarRequest,
  SarAllAccessRequest,
  SarRevokeRequest,
  SarNotifyRequest,
  LicenseEnv,
} from '../types';

const PROD_BASE_URL = process.env.REACT_APP_PROD_API_BASE_URL || 'https://api.rainmaker.espressif.com';
const CUSTOMER_BASE_URL = process.env.REACT_APP_CUSTOMER_API_BASE_URL || 'https://x5ccavw0wf.execute-api.us-east-1.amazonaws.com/dev';

// License module env switching (License module only — SAR/other calls keep CUSTOMER_BASE_URL).
// PROD is the same host as the customer API, so PROD license tokens can be seeded from the
// existing customer login. DEV is a separate environment that requires its own login.
const LICENSE_DEV_BASE_URL = process.env.REACT_APP_DEV_API_BASE_URL || 'https://0zg45xthne.execute-api.us-east-1.amazonaws.com/dev';
export const LICENSE_ENVS: Record<LicenseEnv, string> = {
  PROD: CUSTOMER_BASE_URL,
  DEV: LICENSE_DEV_BASE_URL,
};

export const getLicenseEnv = (): LicenseEnv => {
  const stored = localStorage.getItem('licenseEnv');
  return stored === 'DEV' ? 'DEV' : 'PROD';
};

export const getLicenseBaseUrl = (env: LicenseEnv = getLicenseEnv()): string => LICENSE_ENVS[env];

const licenseTokenKey = (env: LicenseEnv) => `licenseAccessToken_${env.toLowerCase()}`;
const licenseRefreshKey = (env: LicenseEnv) => `licenseRefreshToken_${env.toLowerCase()}`;

export const hasLicenseToken = (env: LicenseEnv = getLicenseEnv()): boolean =>
  !!localStorage.getItem(licenseTokenKey(env));

export const setLicenseEnv = (env: LicenseEnv): void => {
  localStorage.setItem('licenseEnv', env);
  licenseApiClient.defaults.baseURL = LICENSE_ENVS[env];
};

const prodApiClient = axios.create({
  baseURL: PROD_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

const customerApiClient = axios.create({
  baseURL: CUSTOMER_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Dedicated client for the License module so its base URL can switch PROD<->DEV independently.
const licenseApiClient = axios.create({
  baseURL: getLicenseBaseUrl(),
  headers: {
    'Content-Type': 'application/json',
  },
});

// Setup interceptors for production API client
prodApiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('prodAccessToken');
  if (token && config.headers) {
    config.headers.Authorization = token;
  }
  return config;
});

prodApiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      const storedRefreshToken = localStorage.getItem('prodRefreshToken');
      if (storedRefreshToken) {
        try {
          const response = await prodApiClient.post('/v1/login2', { refreshtoken: storedRefreshToken });
          if (response.data.accesstoken) {
            localStorage.setItem('prodAccessToken', response.data.accesstoken);
            if (response.data.refreshtoken) {
              localStorage.setItem('prodRefreshToken', response.data.refreshtoken);
            }
            originalRequest.headers.Authorization = response.data.accesstoken;
            return prodApiClient(originalRequest);
          }
        } catch (refreshError) {
          localStorage.removeItem('prodAccessToken');
          localStorage.removeItem('prodRefreshToken');
          window.location.href = '/login';
        }
      }
    }

    return Promise.reject(error);
  }
);

// Setup interceptors for customer API client
customerApiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('customerAccessToken');
  if (token && config.headers) {
    config.headers.Authorization = token;
  }
  return config;
});

customerApiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      const storedRefreshToken = localStorage.getItem('customerRefreshToken');
      if (storedRefreshToken) {
        try {
          const response = await customerApiClient.post('/v1/login2', { refreshtoken: storedRefreshToken });
          if (response.data.accesstoken) {
            localStorage.setItem('customerAccessToken', response.data.accesstoken);
            if (response.data.refreshtoken) {
              localStorage.setItem('customerRefreshToken', response.data.refreshtoken);
            }
            originalRequest.headers.Authorization = response.data.accesstoken;
            return customerApiClient(originalRequest);
          }
        } catch (refreshError) {
          localStorage.removeItem('customerAccessToken');
          localStorage.removeItem('customerRefreshToken');
          window.location.href = '/login';
        }
      }
    }

    return Promise.reject(error);
  }
);

// Setup interceptors for the license API client (per-env tokens).
licenseApiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem(licenseTokenKey(getLicenseEnv()));
  if (token && config.headers) {
    config.headers.Authorization = token;
  }
  return config;
});

licenseApiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    const env = getLicenseEnv();

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      const storedRefreshToken = localStorage.getItem(licenseRefreshKey(env));
      if (storedRefreshToken) {
        try {
          // Refresh against the env-specific base URL.
          const response = await axios.post(
            `${getLicenseBaseUrl(env)}/v1/login2`,
            { refreshtoken: storedRefreshToken },
            { headers: { 'Content-Type': 'application/json' } }
          );
          if (response.data.accesstoken) {
            localStorage.setItem(licenseTokenKey(env), response.data.accesstoken);
            if (response.data.refreshtoken) {
              localStorage.setItem(licenseRefreshKey(env), response.data.refreshtoken);
            }
            originalRequest.headers.Authorization = response.data.accesstoken;
            return licenseApiClient(originalRequest);
          }
        } catch (refreshError) {
          // Clear only this env's tokens; the UI prompts a re-login for this env.
          localStorage.removeItem(licenseTokenKey(env));
          localStorage.removeItem(licenseRefreshKey(env));
        }
      }
    }

    return Promise.reject(error);
  }
);

// PROD license env shares the customer API host, so reuse the existing customer login tokens.
export const seedLicenseProdTokens = (): void => {
  const accessToken = localStorage.getItem('customerAccessToken');
  const refreshToken = localStorage.getItem('customerRefreshToken');
  if (accessToken && !localStorage.getItem(licenseTokenKey('PROD'))) {
    localStorage.setItem(licenseTokenKey('PROD'), accessToken);
    if (refreshToken) {
      localStorage.setItem(licenseRefreshKey('PROD'), refreshToken);
    }
  }
};

// Authenticate against a specific license env (used for the "re-login if missing" flow,
// typically the DEV env which has no token seeded from the main login).
export const licenseLogin = async (
  env: LicenseEnv,
  credentials: LoginRequest
): Promise<boolean> => {
  const response = await axios.post<LoginResponse>(
    `${getLicenseBaseUrl(env)}/v1/login2`,
    credentials,
    { headers: { 'Content-Type': 'application/json' } }
  );
  if (response.data.status === 'success' && response.data.accesstoken) {
    localStorage.setItem(licenseTokenKey(env), response.data.accesstoken);
    if (response.data.refreshtoken) {
      localStorage.setItem(licenseRefreshKey(env), response.data.refreshtoken);
    }
    return true;
  }
  return false;
};

export const licenseLogout = (env: LicenseEnv): void => {
  localStorage.removeItem(licenseTokenKey(env));
  localStorage.removeItem(licenseRefreshKey(env));
};

export const login = async (request: LoginRequest): Promise<AxiosResponse<LoginResponse>> => {
  // Login to both production and customer APIs
  const [prodResponse, customerResponse] = await Promise.all([
    prodApiClient.post('/v1/login2', request),
    customerApiClient.post('/v1/login2', request)
  ]);

  if (prodResponse.data.status === 'success' && customerResponse.data.status === 'success') {
    // Store both sets of tokens
    if (prodResponse.data.accesstoken) {
      localStorage.setItem('prodAccessToken', prodResponse.data.accesstoken);
      if (prodResponse.data.refreshtoken) {
        localStorage.setItem('prodRefreshToken', prodResponse.data.refreshtoken);
      }
    }

    if (customerResponse.data.accesstoken) {
      localStorage.setItem('customerAccessToken', customerResponse.data.accesstoken);
      if (customerResponse.data.refreshtoken) {
        localStorage.setItem('customerRefreshToken', customerResponse.data.refreshtoken);
      }
    }

    // PROD license env == customer API host, so seed its tokens from the customer login.
    seedLicenseProdTokens();

    // Return production response for backward compatibility
    return prodResponse;
  } else {
    throw new Error(`Login failed: Prod(${prodResponse.status}) Customer(${customerResponse.status})`);
  }
};

// The GET list returns v2 records wrapped in a `customer_license` sub-object, with
// account_id/email/creation_timestamp at the top level. Unwrap it so the rest of the app sees a
// flat license object (v2 carries version:'v2' + batches; v1 records are already flat).
const normalizeLicense = (raw: any): any => {
  if (raw && raw.customer_license && typeof raw.customer_license === 'object') {
    const cl = raw.customer_license;
    return {
      ...cl,
      account_id: cl.account_id ?? raw.account_id,
      email: cl.email ?? raw.email,
      creation_timestamp: cl.creation_timestamp ?? raw.creation_timestamp,
    };
  }
  return raw;
};

// License operations use the License API client (env-switchable PROD/DEV).
// Paginate through all records for an optional set of query params.
const fetchAllLicenses = async (baseParams: Record<string, string> = {}): Promise<LicenseResponse> => {
  let allLicenses: any[] = [];
  let params: Record<string, string> = { ...baseParams };

  while (true) {
    const response = await licenseApiClient.get('/v1/admin/rainmaker_license/', { params });
    const data: LicenseResponse = response.data;

    allLicenses = allLicenses.concat((data.licenses || []).map(normalizeLicense));

    if (data.next_id) {
      params = { ...baseParams, start_id: data.next_id };
    } else {
      break;
    }
  }

  return { licenses: allLicenses, total: allLicenses.length };
};

export const getAllLicenses = async (): Promise<AxiosResponse<LicenseResponse>> => {
  return { data: await fetchAllLicenses() } as AxiosResponse<LicenseResponse>;
};

export const getLicenseByCustomerId = async (
  accountId: string
): Promise<AxiosResponse<LicenseResponse>> => {
  return {
    data: await fetchAllLicenses({ account_id: accountId }),
  } as AxiosResponse<LicenseResponse>;
};

// Search licenses by a single supported key: account_id, fwpn, or email.
export type LicenseSearchField = 'account_id' | 'fwpn' | 'email';

export const searchLicenses = async (
  field: LicenseSearchField,
  value: string
): Promise<AxiosResponse<LicenseResponse>> => {
  return {
    data: await fetchAllLicenses({ [field]: value }),
  } as AxiosResponse<LicenseResponse>;
};

// v1 license create/update (POST). v1 records have no region and use the features[] model.
export const updateLicense = (
  request: LicenseUpdateRequest
): Promise<AxiosResponse<GenericResponse>> => {
  return licenseApiClient.post('/v1/admin/rainmaker_license/', request);
};

export const createNewLicense = (
  request: NewLicenseRequest
): Promise<AxiosResponse<GenericResponse>> => {
  return licenseApiClient.post('/v1/admin/rainmaker_license/', request);
};

// v2 license operations. POST is allowed once (typically via migration); later edits use PUT.
export const getMigrationPayload = (
  accountId: string
): Promise<AxiosResponse<NewV2LicenseRequest>> => {
  return licenseApiClient.get('/v2/admin/rainmaker_license/migrate', {
    params: { account_id: accountId },
  });
};

export const createV2License = (
  request: NewV2LicenseRequest
): Promise<AxiosResponse<GenericResponse>> => {
  return licenseApiClient.post('/v2/admin/rainmaker_license', request);
};

export const updateV2License = (
  accountId: string,
  request: V2LicenseUpdateRequest
): Promise<AxiosResponse<GenericResponse>> => {
  return licenseApiClient.put('/v2/admin/rainmaker_license', request, {
    params: { account_id: accountId },
  });
};

// Quota operations use production API
export const updateQuota = (
  request: QuotaRequest
): Promise<AxiosResponse<GenericResponse>> => {
  return prodApiClient.post('/v1/maintainer/claim/quota', request);
};

// SAR operations use customer API
export const getSarList = (
  accountId: string
): Promise<AxiosResponse<string>> => {
  return customerApiClient.get('/v1/espadmin/saraccess/', {
    params: { account_id: accountId }
  });
};

export const addSarAccess = (
  request: SarRequest
): Promise<AxiosResponse<GenericResponse>> => {
  return customerApiClient.post('/v1/espadmin/saraccess', request);
};

export const addSarAllAccess = (
  request: SarAllAccessRequest,
  skillsAccess: boolean = true
): Promise<AxiosResponse<GenericResponse>> => {
  return customerApiClient.post('/v1/espadmin/saraccess', request, {
    params: { skills_access: skillsAccess }
  });
};

// Revoke SAR access from a customer. Omit repositoryName to revoke all modules,
// or pass a module name to revoke just that single SAR repository.
export const revokeSarAccess = (
  request: SarRevokeRequest,
  repositoryName?: string
): Promise<AxiosResponse<GenericResponse>> => {
  return customerApiClient.delete('/v1/espadmin/saraccess', {
    data: request,
    params: repositoryName ? { repository_name: repositoryName } : undefined,
  });
};

export const sarNotify = async (
  request: SarNotifyRequest,
  sendToPrivate: boolean = true,
  sendToPublic: boolean = false
): Promise<{success: boolean, message: string}> => {
  const promises = [];

  if (sendToPrivate) {
    promises.push(
      customerApiClient.post('/v1/espadmin/release_notification', request)
        .then(() => ({ endpoint: 'Private', success: true, error: null }))
        .catch((error) => ({ endpoint: 'Private', success: false, error }))
    );
  }

  if (sendToPublic) {
    promises.push(
      prodApiClient.post('/v1/espadmin/release_notification', request)
        .then(() => ({ endpoint: 'Public', success: true, error: null }))
        .catch((error) => ({ endpoint: 'Public', success: false, error }))
    );
  }

  const responses = await Promise.all(promises);
  const successfulEndpoints = responses.filter(r => r.success).map(r => r.endpoint);
  const failedEndpoints = responses.filter(r => !r.success);

  if (successfulEndpoints.length === responses.length) {
    return {
      success: true,
      message: `Release notification sent successfully to: ${successfulEndpoints.join(', ')}`
    };
  } else if (successfulEndpoints.length > 0) {
    const failureMessages = failedEndpoints.map(f => 
      `${f.endpoint}: ${f.error?.response?.data?.description || f.error?.message || 'Unknown error'}`
    );
    return {
      success: true,
      message: `Partially successful - Sent to: ${successfulEndpoints.join(', ')}. Failed: ${failureMessages.join(', ')}`
    };
  } else {
    const failureMessages = failedEndpoints.map(f => 
      `${f.endpoint}: ${f.error?.response?.data?.description || f.error?.message || 'Unknown error'}`
    );
    return {
      success: false,
      message: `Failed to send to all endpoints. Errors: ${failureMessages.join(', ')}`
    };
  }
};